import {
    AnswerTypeFrontend,
    QuestionTypeFrontend,
    SessionQuestionEntityFrontend,
} from '../../api/sessionQuestionsApi';

export type EditableSessionQuestion = SessionQuestionEntityFrontend & { answer?: any };

export type SnackbarState = {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
};

// Render order matches the natural flow of the WhatsApp bot:
// language → profession-select → custom-occupation (if "Other") →
// profession-specific questions (e.g. PLUMBER_SYSTEMS, WELDER_TECHNIQUES) →
// "any other professions?" gate → generic personal questions →
// uploads/closing.
// PROFESSION must sit IMMEDIATELY AFTER PROFESSION_SELECTION so the worker
// sees the profession-specific block fill in directly below the picker they
// just used, not at the bottom of the form past every PERSONAL question.
// FINAL_PROFESSION_CONFIRMATION (the "Other professions?" gate) renders here
// to mirror that flow — without it the question is required but invisible,
// which locks submit (the bug the user hit on 2026-06-29).
export const questionTypeOrder: QuestionTypeFrontend[] = [
    QuestionTypeFrontend.LANGUAGE_SELECTION,
    QuestionTypeFrontend.PROFESSION_SELECTION,
    QuestionTypeFrontend.CUSTOM_OCCUPATION_INPUT,
    QuestionTypeFrontend.PROFESSION,
    QuestionTypeFrontend.FINAL_PROFESSION_CONFIRMATION,
    QuestionTypeFrontend.PERSONAL,
    QuestionTypeFrontend.CV_UPLOAD,
    QuestionTypeFrontend.ACTION,
    QuestionTypeFrontend.CLOSING,
];

/**
 * Convert a form value into the wire-format string the bulk-update endpoint
 * accepts. Each answer type has its own serialization rules — keeping them
 * here means hooks and components don't need to know the protocol.
 */
export const normalizeAnswerForSubmission = (
    answerType: AnswerTypeFrontend,
    value: unknown,
): string => {
    if (value === null || value === undefined) {
        return '';
    }

    switch (answerType) {
        case AnswerTypeFrontend.MULTISELECT: {
            if (Array.isArray(value)) {
                return JSON.stringify(value);
            }
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return JSON.stringify(parsed);
                    }
                } catch {
                    const items = value
                        .split(',')
                        .map((item) => item.trim())
                        .filter(Boolean);
                    return JSON.stringify(items);
                }
            }
            return JSON.stringify([]);
        }
        case AnswerTypeFrontend.LIST_OBJECT: {
            if (typeof value === 'string') {
                try {
                    JSON.parse(value);
                    return value;
                } catch {
                    return '[]';
                }
            }
            return JSON.stringify(value);
        }
        case AnswerTypeFrontend.BOOLEAN: {
            if (typeof value === 'boolean') {
                return value ? 'true' : 'false';
            }
            if (typeof value === 'string') {
                const lower = value.toLowerCase();
                if (lower === 'true' || lower === 'false') {
                    return lower;
                }
                if (lower === 'yes') {
                    return 'true';
                }
                if (lower === 'no') {
                    return 'false';
                }
            }
            return '';
        }
        case AnswerTypeFrontend.DATE: {
            if (typeof value === 'string') {
                return value;
            }
            return '';
        }
        default: {
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }
            return String(value);
        }
    }
};

/**
 * Normalize one question while preserving server-owned option keys where the
 * key itself is part of the audited contract. Recruitment consent is seeded
 * as BOOLEAN for bot compatibility, but its reviewed public-form options are
 * versioned by the server and must be submitted as the exact `yes` / `no` key
 * the worker selected rather than rewritten to generic `true` / `false`.
 */
export const normalizeQuestionAnswerForSubmission = (
    question: SessionQuestionEntityFrontend,
    value: unknown,
): string => {
    if (question.questionKey === 'ACTION_CONSENT' && typeof value === 'string') {
        const selectedOption = question.options?.find((option) => option.key === value);
        return selectedOption?.key ?? '';
    }

    return normalizeAnswerForSubmission(question.answerType, value);
};

/**
 * Convert a stored answer (string from the API or projected from the worker
 * entity) into the in-memory shape the form field expects. The inverse of
 * `normalizeAnswerForSubmission`.
 */
export const buildInitialValue = (
    question: SessionQuestionEntityFrontend,
    answer: unknown,
): any => {
    const { answerType } = question;

    if (answer === null || answer === undefined) {
        if (answerType === AnswerTypeFrontend.MULTISELECT) {
            return [];
        }
        if (answerType === AnswerTypeFrontend.LIST_OBJECT) {
            return '[]';
        }
        return '';
    }

    if (question.questionKey === 'ACTION_CONSENT' && typeof answer === 'string') {
        const selectedOption = question.options?.find((option) => option.key === answer);
        return selectedOption?.key ?? '';
    }

    switch (answerType) {
        case AnswerTypeFrontend.MULTISELECT: {
            if (Array.isArray(answer)) {
                return answer.map((item) => String(item));
            }
            if (typeof answer === 'string') {
                try {
                    const parsed = JSON.parse(answer);
                    if (Array.isArray(parsed)) {
                        return parsed.map((item) => String(item));
                    }
                } catch {
                    return answer
                        .split(',')
                        .map((item) => item.trim())
                        .filter(Boolean);
                }
            }
            return [];
        }
        case AnswerTypeFrontend.LIST_OBJECT: {
            if (typeof answer === 'string') {
                return answer;
            }
            try {
                return JSON.stringify(answer);
            } catch {
                return '[]';
            }
        }
        case AnswerTypeFrontend.BOOLEAN: {
            if (typeof answer === 'boolean') {
                return answer ? 'true' : 'false';
            }
            if (typeof answer === 'string') {
                const lower = answer.toLowerCase();
                if (lower === 'true' || lower === 'false') {
                    return lower;
                }
                if (lower === 'yes') {
                    return 'true';
                }
                if (lower === 'no') {
                    return 'false';
                }
            }
            return '';
        }
        case AnswerTypeFrontend.DATE: {
            if (typeof answer === 'string') {
                return answer.split('T')[0];
            }
            return '';
        }
        default: {
            if (typeof answer === 'object') {
                try {
                    return JSON.stringify(answer);
                } catch {
                    return '';
                }
            }
            return String(answer);
        }
    }
};
